import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Users, BookMarked, AlertTriangle, History, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const RETIRE_BADGE = (m) =>
    m <= 12  ? "border-red-500/40 bg-red-500/10 text-red-400"
    : m <= 24 ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
              : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400";

export default function IntellectualCapital() {
    const [data, setData] = useState(null);
    const [heat, setHeat] = useState(null);

    useEffect(() => {
        api.get("/edi/intellectual-capital")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load Intellectual Capital dashboard"));
        api.get("/edi/intellectual-capital/heatmap")
            .then((r) => setHeat(r.data))
            .catch(() => { /* heat map optional */ });
    }, []);

    if (!data) return <div className="kicker text-slate-500">Loading Intellectual Capital…</div>;

    return (
        <div className="space-y-8" data-testid={EDI.intellectualCapitalRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Intellectual Capital Protection™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Ownership, succession and governance of SGS Bateman's engineering
                        knowledge — protecting corporate standards, SME expertise and
                        reuse permissions across every project and client.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Corporate protection</span>
                </div>
            </div>

            {/* Standards */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                    <BookMarked className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <h3 className="font-display font-black text-white tracking-tight">Corporate Engineering Standards</h3>
                    <div className="flex-1" />
                    <span className="kicker text-[10px] text-slate-500">{data.corporate_standards.length} standards</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                <th className="text-left px-4 py-3">Reference</th>
                                <th className="text-left px-4 py-3">Title</th>
                                <th className="text-right px-4 py-3">Health</th>
                                <th className="text-left px-4 py-3">Owner</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.corporate_standards.map((s) => (
                                <tr key={s.standard} className="border-t border-slate-800/60">
                                    <td className="px-4 py-2 text-slate-100">{s.standard}</td>
                                    <td className="px-4 py-2 text-slate-300">{s.title}</td>
                                    <td className="px-4 py-2 text-right">
                                        <span className="text-[var(--sgsb-gold)] font-display font-bold">{s.health}%</span>
                                    </td>
                                    <td className="px-4 py-2 text-slate-300">{s.owner}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SME map + Governance */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl overflow-hidden xl:col-span-2">
                    <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                        <Users className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <h3 className="font-display font-black text-white tracking-tight">SME Map · Retirement Risk</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Discipline</th>
                                    <th className="text-left px-4 py-3">SME</th>
                                    <th className="text-right px-4 py-3">Assets Owned</th>
                                    <th className="text-left px-4 py-3">Successor</th>
                                    <th className="text-right px-4 py-3">Horizon</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.sme_map.map((s) => (
                                    <tr key={s.discipline} className="border-t border-slate-800/60">
                                        <td className="px-4 py-2 text-slate-100">{s.discipline}</td>
                                        <td className="px-4 py-2 text-slate-300">{s.sme}</td>
                                        <td className="px-4 py-2 text-right text-slate-200">{s.assets_owned}</td>
                                        <td className="px-4 py-2 text-slate-300 capitalize">{s.successor}</td>
                                        <td className="px-4 py-2 text-right">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border ${RETIRE_BADGE(s.retirement_horizon_m)}`}>
                                                <AlertTriangle className="h-3 w-3" strokeWidth={2} />
                                                {s.retirement_horizon_m} mo
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-2">
                        <History className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <h3 className="font-display font-black text-white tracking-tight">Governance</h3>
                    </div>
                    <div className="mt-4">
                        <div className="kicker text-slate-500 text-[10px] mb-2">Reuse permissions</div>
                        {data.governance.reuse_permissions.map((p) => (
                            <div key={p.scope} className="flex items-center justify-between border-t border-slate-800/60 py-2">
                                <div className="text-sm text-slate-200">{p.scope}</div>
                                <span className="rounded-full bg-slate-800/60 border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300">{p.status}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                            <div className="kicker text-slate-500 text-[10px]">Audit events (90d)</div>
                            <div className="mt-1 font-display font-black text-white text-lg tracking-tight">
                                {data.governance.audit_trail.events_last_90d.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-emerald-400 font-body">
                                {data.governance.audit_trail.high_severity} high severity
                            </div>
                        </div>
                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                            <div className="kicker text-slate-500 text-[10px]">Version drift (90d)</div>
                            <div className="mt-1 font-display font-black text-white text-lg tracking-tight">
                                {data.governance.version_governance.drift_events_last_90d}
                            </div>
                            <div className="text-[11px] text-emerald-400 font-body">
                                {data.governance.version_governance.auto_resolved} auto-resolved
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Heat Map */}
            {heat && (
                <div className="glass rounded-xl overflow-hidden" data-testid={EDI.icHeatmapRoot}>
                    <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                        <Flame className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <h3 className="font-display font-black text-white tracking-tight">Intellectual Capital Heat Map™</h3>
                        <div className="flex-1" />
                        <span className="kicker text-[10px] text-slate-500">
                            {heat.rows.length} disciplines · at-risk (12m): {heat.total_at_risk_12m}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Discipline</th>
                                    {heat.cols.map((c) => <th key={c} className="text-center px-4 py-3">{c}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {heat.rows.map((r) => (
                                    <tr key={r.discipline} className="border-t border-slate-800/60">
                                        <td className="px-4 py-2 text-slate-100">{r.discipline}</td>
                                        {r.cells.map((v, i) => {
                                            const ratio = heat.max_cell ? Math.min(1, v / heat.max_cell) : 0;
                                            // Colour ramp: <12mo red, <24mo amber, <48mo yellow, >48mo emerald
                                            const rampColour = i === 0
                                                ? `rgba(239, 68, 68, ${0.15 + ratio * 0.7})`
                                                : i === 1
                                                    ? `rgba(245, 158, 11, ${0.15 + ratio * 0.65})`
                                                    : i === 2
                                                        ? `rgba(234, 179, 8, ${0.12 + ratio * 0.55})`
                                                        : `rgba(16, 185, 129, ${0.10 + ratio * 0.5})`;
                                            return (
                                                <td key={i} className="px-3 py-2 text-center">
                                                    <div className="inline-flex items-center justify-center min-w-[60px] rounded-md py-1.5 px-3 text-white font-display font-bold text-sm"
                                                         style={{ background: rampColour }}>
                                                        {v}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border-t border-slate-800/60">
                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                            <div className="kicker text-slate-500 text-[10px]">Critical Disciplines</div>
                            <div className="mt-1 text-slate-100 font-body">{heat.critical_disciplines.join(", ")}</div>
                        </div>
                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                            <div className="kicker text-slate-500 text-[10px]">Succession Readiness</div>
                            <div className="mt-1 font-display font-black text-[var(--sgsb-gold)] text-xl">{heat.succession_readiness_pct}%</div>
                        </div>
                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                            <div className="kicker text-slate-500 text-[10px]">Corporate Standard Coverage</div>
                            <div className="mt-1 font-display font-black text-emerald-400 text-xl">{heat.corporate_standard_coverage_pct}%</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
